/**
 * Axios client.
 *
 * Mirrors the BenchTime convention: the API speaks snake_case, the UI speaks
 * camelCase, and the boundary is these two interceptors. Keeping it here means
 * no component ever has to think about casing.
 */
import axios, {
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';

// Augmenting the config type rather than casting at each call site: an
// opt-out that only exists as a cast is one a future caller cannot find.
declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Do not narrow this request to the owner's acting company. */
    skipActingCompany?: boolean;
  }
}

const ACCESS_KEY = 'ats_access_token';
const REFRESH_KEY = 'ats_refresh_token';

export const tokenStore = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/**
 * Which company an owner is acting as, pushed here by the auth store.
 *
 * Held in this module rather than read from the store because the store
 * imports this file — reaching back the other way would be a cycle. A super
 * admin belongs to no company, so without this every company-scoped write is
 * refused with "super admin must specify a company_id"; setting it once here
 * is what saves a selector on ten separate forms.
 */
let actingCompanyId: string | null = null;

export function setActingCompanyForRequests(companyId: string | null): void {
  actingCompanyId = companyId;
}

const snake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const camel = (s: string) => s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

/**
 * Fields whose value is a map keyed by *data*, not by field name.
 *
 * Everything else here is a schema: `full_name` becomes `fullName` because
 * both sides agreed on that name in advance. A custom field's values are not —
 * they are keyed by field id, and an id is content that happens to be sitting
 * in a key position.
 *
 * The damage is asymmetric, which is what makes it easy to miss. Going out,
 * `snake('cf_a1b2c3')` finds no capitals and changes nothing, so writes appear
 * to work. Coming back, `camel('cf_a1b2c3')` matches `_a` and returns
 * `cfA1b2c3` — so a value is saved under one key and read under another, and
 * the form renders empty with no error anywhere.
 *
 * Listed under both spellings because this runs in both directions.
 */
const OPAQUE_KEYED = new Set(['custom_values', 'customValues']);

function convertKeys(value: unknown, transform: (key: string) => string): unknown {
  if (Array.isArray(value)) return value.map((v) => convertKeys(v, transform));
  // Date and File must pass through untouched — spreading them destroys them.
  if (value instanceof Date || value instanceof File || value instanceof Blob) return value;
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        transform(k),
        // The key itself is still converted; only what hangs under it is
        // spared, because those keys are ids rather than field names.
        OPAQUE_KEYED.has(k) ? v : convertKeys(v, transform),
      ]),
    );
  }
  return value;
}

export const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  // Correct for every JSON call, and a trap for the few that are not: axios
  // only generates a multipart Content-Type *and its boundary* when the header
  // is unset, so this default silently suppresses it for FormData. The
  // interceptor below clears it again for those. Do not remove that without
  // removing this.
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.access();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Applied before the casing conversion below, so it arrives as company_id.
  // An explicit value always wins: a caller that named a company meant it.
  // skipActingCompany exists for the owner's cross-company screens, where
  // narrowing to one company is precisely the wrong answer.
  const params = (config.params ?? {}) as Record<string, unknown>;
  if (
    actingCompanyId
    && !config.skipActingCompany
    && params.companyId === undefined
    && params.company_id === undefined
  ) {
    config.params = { ...params, companyId: actingCompanyId };
  }

  if (config.data instanceof FormData) {
    // A file upload. Two things must not happen to it.
    //
    // Its keys are left alone — the caller writes them in snake_case by hand,
    // because converting a FormData means rebuilding it and File entries do
    // not survive that.
    //
    // And the instance's application/json default is removed, so axios
    // generates the multipart type and, critically, the boundary. Left in
    // place the body goes out labelled JSON with no boundary, Starlette
    // declines to parse it as a form, and FastAPI answers 422 "field
    // required" for a file that was in fact attached — an error describing
    // the opposite of what happened. Documents shipped with that bug and
    // Imports nearly did; fixing it per-caller only arms the trap for the
    // next one.
    config.headers.delete('Content-Type');
  } else if (config.data) {
    config.data = convertKeys(config.data, snake);
  }
  if (config.params) {
    config.params = convertKeys(config.params, snake);
  }
  return config;
});

/**
 * Refresh-on-401, with a single-flight guard.
 *
 * Without the guard, a page issuing six parallel queries on an expired token
 * would fire six refreshes. Because refresh tokens rotate, five of those would
 * be treated as replay and revoke the whole session — logging the user out for
 * doing nothing wrong.
 */
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenStore.refresh();
  if (!refreshToken) return null;

  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_API_BASE_URL || ''}/api/v1/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );
    tokenStore.set(data.access_token, data.refresh_token);
    return data.access_token as string;
  } catch {
    tokenStore.clear();
    return null;
  }
}

client.interceptors.response.use(
  (response) => {
    response.data = convertKeys(response.data, camel);
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const isAuthCall = original?.url?.includes('/auth/login')
      || original?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      refreshing = refreshing ?? refreshAccessToken().finally(() => { refreshing = null; });
      const token = await refreshing;

      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return client(original);
      }

      tokenStore.clear();
      // Respect the configured base path so a sub-path deployment does not
      // redirect out of its own mount point.
      window.location.href = `${import.meta.env.BASE_URL || '/'}login`.replace('//', '/');
    }

    return Promise.reject(error);
  },
);

/** Turn a FastAPI error payload into something worth showing a user. */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const first = detail[0] as { msg?: string; loc?: string[] } | undefined;
      if (first?.msg) {
        const field = first.loc?.slice(1).join('.');
        return field ? `${field}: ${first.msg}` : first.msg;
      }
    }
    if (error.code === 'ERR_NETWORK') return 'Cannot reach the server';
  }
  return fallback;
}
